import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva } from "class-variance-authority";
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref} />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        // H1 (#423) — LA HOJA DE ABAJO RESPETA LA BARRITA DE INICIO.
        //
        // Lo último de una hoja inferior son sus botones, y en un iPhone con
        // barrita de inicio quedaban a 40 px del filo (24 del `p-6` de la base +
        // 16 del `pb-4` del consumidor) SIN descontar los 34 de la barrita: el
        // sistema se los comia por debajo. Esta clase los sube esos 34, y ni un
        // pixel más: `env()` COLAPSA A 0 en escritorio y en la web, donde
        // `calc(1.5rem + 0px)` son los MISMOS 24 px del `p-6` que sustituye.
        //
        // VA EN LA VARIANTE Y NO EN LA BASE, y es load-bearing: `cva` concatena
        // base-y-luego-variante y `cn` es `twMerge`, así que un `pb-*` escrito
        // AQUÍ llega DESPUÉS del `p-6` y gana — sobrescribiendo sólo el
        // acolchado de abajo. En la base, delante del `p-6`, twMerge lo
        // BORRARÍA; detrás, se lo aplicaría también a `top`, `left` y `right`,
        // que es alcance de más. Ningún consumidor pasa acolchado por
        // `className`, así que aguas abajo no hay con quién chocar.
        //
        // La forma de la clase es la que el repo ya usa para esto mismo
        // (`JoinGroupPage.js`, `ReferralLandingPage.js`): la SUMA, no el
        // máximo — aquí el acolchado propio de la hoja y la zona segura son dos
        // cosas distintas y las dos tienen que caber.
        //
        // #460 (hoja-temas, 23-sep) -- 12 PT DE AIRE SOBRE LA BARRITA, 16 SIN ELLA.
        // Los 24 px de #423 mas la zona se veian como un hueco negro bajo el
        // ultimo boton (en el Pro Max, 74 px bajo la caja de "Limpiar"). Ahora
        // el acolchado de abajo es el MAYOR de dos: 16 px al borde (un iPhone sin
        // barrita, la web, el escritorio) o 12 px mas la zona de la barrita. Sigue
        // siendo la suma del aire y la zona cuando hay barrita; el piso de 16 es
        // para que sin ella el ultimo boton no quede pegado al filo. Y sigue en
        // la VARIANTE, por la misma razon de arriba.
        bottom:
          "inset-x-0 bottom-0 border-t pb-[max(1rem,calc(0.75rem+env(safe-area-inset-bottom,0px)))] data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

// TANDA 24 (A2.14): gemela de la de `dialog.jsx`. Misma etiqueta, misma llave,
// misma razón: era la única palabra que un lector de pantalla anunciaba de esa
// X, y estaba en inglés en todas las hojas del producto.
const SheetContent = React.forwardRef(({ side = "right", className, children, ...props }, ref) => {
  const { t } = useTranslation();
  return (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
      {/* R-10 (censo de X) — la X que trae de fábrica el componente de la casa es
          un ícono de 16px sin acolchado: 16pt de blanco para el dedo. Se le suma
          la zona de 44pt de `.modal-close-tap` (index.css), que es un
          pseudo-elemento: no pinta, no ocupa lugar y no mueve el ícono ni un
          pixel, así que ninguna hoja que use <Sheet> cambia de aspecto. Hoy la
          única es la hoja de "ir a la pregunta" del simulador, cuyo título es
          corto y va a la izquierda — no había encimamiento que corregir. */}
      <SheetPrimitive.Close
        className="modal-close-tap absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
        <X className="h-4 w-4" />
        <span className="sr-only">{t('common:a11y.close')}</span>
      </SheetPrimitive.Close>
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
  );
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-2 text-center sm:text-left", className)}
    {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
